import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Typography,
  Button,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
  Grid,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

/**
 * Extensible LoginForm component with configurable password policies
 * Props:
 *  - title: string
 *  - description: string
 *  - onForgotPassword: func
 *  - idpList: array of { display_name }
 *  - submitText: string
 *  - onLoginClick: func(values)
 *  - showForgotPassword: bool
 *  - registerLink: { text, href }
 */
const LoginForm = ({
  onForgotPassword = () => {},
  idpList,
  submitText = 'Sign In',
  onLoginClick,
  showForgotPassword = true,
  onIDPLogin,
  onlyADLoginEnabled = false,
}) => {
  const [activeView, setActiveView] = useState('login');
  const [showPassword, setShowPassword] = useState(false);

  // Build Yup schema for fields including dynamic password rules
  const loginFormik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().required('Required'),
      password: Yup.string().required('Required'),
    }),
    onSubmit: (values) => onLoginClick(values),
  });

  const forgotPasswordFormik = useFormik({
    initialValues: { email: '' },
    validationSchema: Yup.object().shape({
      email: Yup.string().email('Invalid email address').required('Required'),
    }),
    onSubmit: (values) => {
      onForgotPassword(values.email);
    },
  });
  const handleClickShowPassword = useCallback(
    () => setShowPassword((show) => !show),
    []
  );
  const handleMouseDownPassword = useCallback(
    (event) => event.preventDefault(),
    []
  );

  const handleClickForgotPassword = useCallback(() => {
    setActiveView('forgotPassword');
  }, []);

  const handleClickBackToLogin = useCallback(() => {
    setActiveView('login');
  }, []);

  return (
    <Grid
      sx={(theme) => ({
        p: 4,
        width: 450,
        border: `1px solid ${theme.palette?.loginPage?.loginFormBorder}`,
        borderRadius: 2,
        backgroundColor: theme.palette?.loginPage?.background,
      })}
    >
      {activeView === 'login' && (
        <Stack spacing={3}>
          <Typography variant="h2">Sign in to your account</Typography>

          {!onlyADLoginEnabled && (
            <Stack spacing={3}>
              <Box>
                <TextField
                  required
                  fullWidth
                  name="email"
                  label="Email"
                  placeholder="Enter your email"
                  type="email"
                  value={loginFormik.values.email}
                  onChange={loginFormik.handleChange}
                  onBlur={loginFormik.handleBlur}
                  error={
                    loginFormik.touched.email &&
                    Boolean(loginFormik.errors.email)
                  }
                  helperText={
                    loginFormik.touched.email && loginFormik.errors.email
                  }
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginFormik.values.password}
                  onChange={loginFormik.handleChange}
                  onBlur={loginFormik.handleBlur}
                  error={
                    loginFormik.touched.password &&
                    Boolean(loginFormik.errors.password)
                  }
                  helperText={
                    loginFormik.touched.password && loginFormik.errors.password
                  }
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              {showForgotPassword && (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <Button
                    onClick={handleClickForgotPassword}
                    sx={{
                      textTransform: 'none',
                      color: 'primary.main',
                      '&:hover': {
                        textDecoration: 'underline',
                        backgroundColor: 'transparent',
                      },
                      marginTop: -2,
                      marginBottom: -2,
                    }}
                  >
                    Forgot Password?
                  </Button>
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                onClick={loginFormik.handleSubmit}
              >
                {submitText}
              </Button>
            </Stack>
          )}

          {(idpList || []).length > 0 && (
            <>
              {!onlyADLoginEnabled && (
                <Divider
                  sx={{
                    my: 2,
                  }}
                >
                  Or sign in with
                </Divider>
              )}
              <Stack spacing={1}>
                {idpList?.map(({ display_name }) => (
                  <Button
                    key={display_name}
                    variant="outlined"
                    fullWidth
                    onClick={onIDPLogin}
                  >
                    {display_name || 'IDP Login'}
                  </Button>
                ))}
              </Stack>
            </>
          )}
        </Stack>
      )}
      {activeView === 'forgotPassword' && (
        <Stack spacing={2}>
          <Typography variant="h1" pb={2}>
            Forgot Password
          </Typography>
          <Typography variant="subtitle2" pb={2}>
            Enter your email to reset your password
          </Typography>

          <TextField
            required
            fullWidth
            name="email"
            label="Email"
            type="email"
            placeholder="Enter your email"
            value={forgotPasswordFormik.values.email}
            onChange={forgotPasswordFormik.handleChange}
            onBlur={forgotPasswordFormik.handleBlur}
            error={
              forgotPasswordFormik.touched.email &&
              Boolean(forgotPasswordFormik.errors.email)
            }
            helperText={
              forgotPasswordFormik.touched.email &&
              forgotPasswordFormik.errors.email
            }
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <Box pt={1} display="flex" flexDirection="column" gap={1}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              onClick={forgotPasswordFormik.handleSubmit}
              title="Reset"
            >
              Reset Password
            </Button>

            <Button
              variant="outlined"
              onClick={handleClickBackToLogin}
              size="large"
              fullWidth
            >
              Back to Login
            </Button>
          </Box>
        </Stack>
      )}
    </Grid>
  );
};

LoginForm.propTypes = {
  showForgotPassword: PropTypes.bool,
  onForgotPassword: PropTypes.func,
  idpList: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string.isRequired,
    })
  ),
  submitText: PropTypes.string,
  onLoginClick: PropTypes.func.isRequired,
  registerLink: PropTypes.shape({
    text: PropTypes.string,
    href: PropTypes.string,
  }),
  onIDPLogin: PropTypes.func,
  onlyADLoginEnabled: PropTypes.bool,
};

LoginForm.defaultProps = {
  onForgotPassword: null,
  idpList: [],
  submitText: 'Sign In',
  showForgotPassword: true,
  onIDPLogin: () => {},
  registerLink: null,
  onlyADLoginEnabled: false,
};

export default LoginForm;
